import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Loader2, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null); setMissing(false);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    if (article) document.title = `${article.title} | Futuregen Innovations`;
  }, [article]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: article.title, url }); } catch (err) { if (err?.name !== "AbortError") logError("Share failed", err); } }
    else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  if (missing) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="article-missing">
      <h1 className="font-display text-2xl font-bold mb-3">Article not found</h1>
      <Button asChild variant="outline"><Link to="/guidance">Back to guidance</Link></Button>
    </div>
  );
  if (!article) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="article-page">
      <Link to="/guidance" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="article-back">
        <ArrowLeft className="w-4 h-4" /> All guidance
      </Link>
      <div className="flex items-center gap-2 mb-4">
        <Badge variant="secondary">{article.category}</Badge>
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.reading_minutes} min read</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-4" data-testid="article-title">{article.title}</h1>
      <p className="text-muted-foreground text-base md:text-lg mb-6">{article.excerpt}</p>
      <div className="flex items-center justify-between border-y border-border py-3 mb-10 text-sm">
        <span className="text-muted-foreground">{article.author} · {fmt(article.published_at)}</span>
        <Button variant="ghost" size="sm" onClick={share} data-testid="article-share-button"><Share2 className="w-4 h-4 mr-1.5" /> Share</Button>
      </div>
      <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-primary" data-testid="article-body">
        <ReactMarkdown>{article.body}</ReactMarkdown>
      </div>
      <div className="mt-14 rounded-2xl bg-primary text-primary-foreground p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Sparkles className="w-8 h-8 shrink-0" />
        <div className="flex-1">
          <p className="font-display font-bold text-lg">Apply this to your service</p>
          <p className="text-sm text-primary-foreground/85">Ask the AI Compliance Assistant how this guidance affects your setting — a specialist steps in when needed.</p>
        </div>
        <Button asChild variant="secondary" data-testid="article-assistant-cta"><Link to="/assistant">Ask the assistant</Link></Button>
      </div>
    </article>
  );
}
